/* eslint-disable react/no-array-index-key */
/* eslint-disable import/no-unresolved */
import 'swiper/css';
import 'swiper/css/free-mode';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import type SwiperCore from 'swiper';
import { Lazy } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { chunk } from '~/utils/array';

import SwipeController from './SwipeController';

import styles from './index.module.css';

interface Props<T> {
  list: T[];
  rowCount?: number;
  columnCount?: number;
  initialSlide?: number;
  renderToProduct: (product: T) => ReactNode;
  onSlideChangeCallback?: () => void;
}

const InfiniteSwipeShowcase = <T extends unknown>({
  list,
  rowCount = 2,
  columnCount = 3,
  initialSlide = 0,
  renderToProduct,
  onSlideChangeCallback,
}: Props<T>) => {
  const swiperRef = useRef<SwiperCore>();
  const [realIndex, setRealIndex] = useState(0);
  const handlePrev = () => {
    swiperRef.current?.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  const offset = rowCount * columnCount;
  const chunkedList = chunk(list, offset); // [Array, Array, Array, ...]
  const totalPageIndex = chunkedList.length;
  const buttonVisible = totalPageIndex !== 1;

  return (
    <Swiper
      slidesPerView={1}
      modules={[Lazy]}
      initialSlide={initialSlide}
      onBeforeInit={(swiper) => {
        swiperRef.current = swiper;
      }}
      onSlideChangeTransitionStart={(swiper) => {
        setRealIndex(swiper.realIndex);

        if (realIndex !== swiper.realIndex) onSlideChangeCallback?.();
      }}
      lazy
      loop={totalPageIndex > 1}
    >
      {chunkedList.map((productList, index) => {
        const rows = chunk(productList, columnCount); // [Array, Array, ...]

        return (
          <SwiperSlide key={index}>
            <div>
              {rows.map((row, rowIndex) => (
                <div className={styles.swiperRow} key={rowIndex}>
                  {row.map((column, columnIndex) => {
                    const maxWidth = `${(100 / columnCount).toFixed(2)}%`;

                    return (
                      <div
                        style={{
                          maxWidth,
                        }}
                        key={columnIndex}
                      >
                        {renderToProduct(column)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </SwiperSlide>
        );
      })}
      <SwipeController
        realIndex={realIndex}
        totalPageIndex={totalPageIndex}
        visible={buttonVisible}
        handlePrev={handlePrev}
        handleNext={handleNext}
      />
    </Swiper>
  );
};

export default InfiniteSwipeShowcase;
